import dayjs from 'dayjs';
import cloneDeep from 'lodash.clonedeep';
import { mergeAndRemoveDuplicates } from './merge';
import { makePolicyDetailsFromSegments as farmMakePolicyDetailsFromSegments } from '@ourbranch/farm-utils';
import { isFarmBasedPolicyType } from '@ourbranch/policy-types';

// The first segment is always included regardless of `activeSegmentsOnly` and `activeOnDate`,
export function policyDetailsFromSegments(
  policy: Record<string, any>,
  {
    activeSegmentsOnly = false,
    activeOnDate = '',
    ignoreRenewalSegments = true,
    upToSegmentIndex = undefined,
    upToSegmentId = undefined,
    keepPremium = false
  } = {}
) {
  if (isFarmBasedPolicyType(policy.policyType)) {
    return farmMakePolicyDetailsFromSegments(<any>policy, policy.segments, {
      activeSegmentsOnly,
      activeOnDate,
      ignoreRenewalSegments,
      upToSegmentIndex,
      upToSegmentId,
      keepPremium
    });
  }

  // Resolve the stop index once, up front. When the caller targets the inception
  // segment (upToSegmentId matching segments[0]), stopIndex is 0 and no later
  // segments get merged — preserving the inception state.
  let stopIndex = policy.segments.length - 1;
  if (upToSegmentId !== undefined) {
    const targetIndex = policy.segments.findIndex((s: any) => s.segmentId === upToSegmentId);
    if (targetIndex >= 0) {
      stopIndex = targetIndex;
    }
  }
  if (upToSegmentIndex !== undefined) {
    stopIndex = Math.min(stopIndex, upToSegmentIndex);
  }

  const policyDetails = cloneDeep(policy.segments[0]);
  for (let i = 1; i <= stopIndex; i += 1) {
    if (activeSegmentsOnly && dayjs(policy.segments[i].startDate).isAfter(dayjs())) {
      // Ignore any segments that start in the future if `activeSegmentsOnly` is true
      continue;
    }

    if (activeOnDate && dayjs(policy.segments[i].startDate).isAfter(dayjs(activeOnDate))) {
      // for claims only show policy segments that are active on date of loss
      continue;
    }

    if (ignoreRenewalSegments && dayjs(policy.segments[i].startDate).isAfter(policy.endDate)) {
      // ignore renewal segments
      continue;
    }

    mergeAndRemoveDuplicates(policyDetails, policy.segments[i]);
  }

  if (policyDetails?.premium && !keepPremium) {
    // we don't want the segment details premium to override policy.premium
    // and we copy the elements here to the root of policy
    delete policyDetails.premium;
  }
  return policyDetails;
}
